import { cn } from '@/shared/lib/cn';

export interface PaginationProps {
  /** Página atual (1-based). */
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

const ICON_BUTTON =
  'flex size-8 shrink-0 items-center justify-center rounded-xl text-ink-muted transition-colors hover:bg-tag/12 disabled:pointer-events-none disabled:opacity-48';

/**
 * Paginação numérica (Figma FGP "pagination-1"): First/Prev/[até 5 páginas]/Next/Last,
 * página atual em destaque roxo. Janela de 5 páginas centrada na atual.
 */
export function Pagination({ page, totalPages, onPageChange, className }: PaginationProps) {
  if (totalPages <= 1) return null;

  const windowStart = Math.max(1, Math.min(page - 2, totalPages - 4));
  const pageNumbers = Array.from({ length: Math.min(5, totalPages) }, (_, i) => windowStart + i);

  return (
    <nav aria-label="Paginação" className={cn('flex items-center gap-0', className)}>
      <button
        type="button"
        disabled={page === 1}
        onClick={() => onPageChange(1)}
        className={ICON_BUTTON}
        aria-label="Primeira página"
      >
        <img src="/icons/chevron-first-16.svg" alt="" className="size-4" />
      </button>
      <button
        type="button"
        disabled={page === 1}
        onClick={() => onPageChange(Math.max(1, page - 1))}
        className={ICON_BUTTON}
        aria-label="Página anterior"
      >
        <img src="/icons/chevron-left-16.svg" alt="" className="size-4" />
      </button>

      {pageNumbers.map((pageNum) => {
        const active = pageNum === page;
        return (
          <button
            key={pageNum}
            type="button"
            onClick={() => onPageChange(pageNum)}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-xl text-sm font-semibold transition-colors',
              active ? 'bg-brand-solid text-white' : 'cursor-pointer text-ink-muted hover:bg-tag/12',
            )}
          >
            {pageNum}
          </button>
        );
      })}

      <button
        type="button"
        disabled={page === totalPages}
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        className={ICON_BUTTON}
        aria-label="Próxima página"
      >
        <img src="/icons/chevron-right-16.svg" alt="" className="size-4" />
      </button>
      <button
        type="button"
        disabled={page === totalPages}
        onClick={() => onPageChange(totalPages)}
        className={ICON_BUTTON}
        aria-label="Última página"
      >
        <img src="/icons/chevron-last-16.svg" alt="" className="size-4" />
      </button>
    </nav>
  );
}
